<script setup>
/**
 * TestimonialWall v2 — editorial "voice" treatment.
 *
 * Bespoke moves NOT used elsewhere on the landing:
 *  - Big decorative serif quote mark anchoring the top-left of each card.
 *  - Quote body set in serif italic (different family from the rest of the
 *    page, which is sans Inter) — gives the testimonial the feel of a
 *    citation in a magazine, not a feature card.
 *  - One key phrase per quote highlighted with a marker-style yellow swipe
 *    (.text-highlight) — affirmative emphasis, the opposite of the
 *    strikethrough used in the hero.
 *  - Metric pulled out as an "editor's note" footer: dashed cyan border
 *    above + arrow → after the figure. Not a chip at the top.
 *  - Top-right editorial pagination "01 / 03" in mono.
 *  - Subtle per-card rotation (±0.4°) for an almost imperceptible
 *    physicality, like cited clippings.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowRight } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();

// Rotation removed: it shifted the absolute-positioned quote mark in each
// card and broke top-edge alignment. Premium-feel comes from the typography
// and highlighter alone — no need for the physicality trick here.
const testimonials = computed(() => [
  {
    quotePre: t("landing.testimonials.items.0.quote_pre"),
    quoteHighlight: t("landing.testimonials.items.0.quote_highlight"),
    quotePost: t("landing.testimonials.items.0.quote_post"),
    metric: t("landing.testimonials.items.0.metric"),
    name: t("landing.testimonials.items.0.name"),
    role: t("landing.testimonials.items.0.role"),
    company: t("landing.testimonials.items.0.company"),
    initials: t("landing.testimonials.items.0.initials"),
  },
  {
    quotePre: t("landing.testimonials.items.1.quote_pre"),
    quoteHighlight: t("landing.testimonials.items.1.quote_highlight"),
    quotePost: t("landing.testimonials.items.1.quote_post"),
    metric: t("landing.testimonials.items.1.metric"),
    name: t("landing.testimonials.items.1.name"),
    role: t("landing.testimonials.items.1.role"),
    company: t("landing.testimonials.items.1.company"),
    initials: t("landing.testimonials.items.1.initials"),
  },
  {
    quotePre: t("landing.testimonials.items.2.quote_pre"),
    quoteHighlight: t("landing.testimonials.items.2.quote_highlight"),
    quotePost: t("landing.testimonials.items.2.quote_post"),
    metric: t("landing.testimonials.items.2.metric"),
    name: t("landing.testimonials.items.2.name"),
    role: t("landing.testimonials.items.2.role"),
    company: t("landing.testimonials.items.2.company"),
    initials: t("landing.testimonials.items.2.initials"),
  },
]);
</script>

<template>
  <section class="py-24 bg-background">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-14 max-w-2xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
          {{ t("landing.testimonials.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
          {{ t("landing.testimonials.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.testimonials.subtitle") }}
        </p>
      </AnimatedSection>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7 max-w-6xl mx-auto">
        <AnimatedSection
          v-for="(it, idx) in testimonials"
          :key="idx"
          animation="fade-up"
          :delay="idx * 130"
        >
          <!-- Card uses flex column so the author footer + metric callout
               are pinned to the bottom regardless of quote length. The
               <blockquote> grows with flex-1 to absorb the variable height. -->
          <article
            class="relative h-full p-7 lg:p-8 rounded-2xl card-elevated-premium overflow-hidden flex flex-col"
          >
            <!-- Big decorative serif quote mark, anchoring top-left.
                 Low opacity, purely decorative — provides "voice" cue. -->
            <span
              aria-hidden="true"
              class="absolute top-2 left-5 text-[120px] leading-none text-accent/15 select-none pointer-events-none"
              style="font-family: 'Source Serif Pro', Georgia, 'Times New Roman', serif;"
            >“</span>

            <!-- Editorial pagination, top-right -->
            <span class="absolute top-5 right-6 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums">
              {{ String(idx + 1).padStart(2, "0") }} / {{ String(testimonials.length).padStart(2, "0") }}
            </span>

            <!-- Quote in serif italic — different family creates "voice".
                 flex-1 makes it absorb available height so the footer below
                 lines up across cards regardless of quote length. -->
            <blockquote
              class="relative pt-14 mb-6 text-[17px] leading-relaxed text-foreground italic flex-1"
              style="font-family: 'Source Serif Pro', Georgia, 'Times New Roman', serif;"
            >
              <span>{{ it.quotePre }}</span>
              <span class="text-highlight font-medium not-italic">{{ it.quoteHighlight }}</span>
              <span>{{ it.quotePost }}</span>
            </blockquote>

            <!-- Author footer: avatar + name + role/company.
                 mt-auto is redundant with flex-1 above, but keeps intent clear. -->
            <footer class="flex items-center gap-3 pt-5 border-t border-border">
              <div class="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center text-xs font-bold text-foreground flex-shrink-0">
                {{ it.initials }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-foreground truncate">
                  {{ it.name }}
                </p>
                <p class="text-xs text-muted-foreground truncate">
                  {{ it.role }} · <span class="font-medium">{{ it.company }}</span>
                </p>
              </div>
            </footer>

            <!-- Editor's note metric callout — dashed cyan rule + arrow.
                 Replaces the top chip pattern; reads as a footnote. -->
            <div class="mt-5 pt-4 border-t border-dashed border-accent/30 flex items-center justify-end gap-1.5">
              <span class="text-sm font-bold text-accent tabular-nums">
                {{ it.metric }}
              </span>
              <ArrowRight class="w-3.5 h-3.5 text-accent" />
            </div>
          </article>
        </AnimatedSection>
      </div>

      <AnimatedSection animation="fade-up" :delay="450" class="text-center mt-10">
        <a
          href="#"
          class="inline-flex items-center gap-1.5 text-sm text-accent hover:text-foreground transition-colors font-medium"
        >
          {{ t("landing.testimonials.tail_link") }}
          <span aria-hidden="true">→</span>
        </a>
      </AnimatedSection>
    </div>
  </section>
</template>
